<script lang="ts">
  /**
   * LetterModeScreen - Main gameplay screen for Letter Mode
   * Player controls a letter and collects matching objects
   */

  import { onMount, onDestroy } from 'svelte';
  import { gameState } from '$lib/stores/gameState';
  import { gameEngine } from '$lib/systems/gameEngine';
  import { inputSystem } from '$lib/systems/inputSystem';
  import { collisionSystem } from '$lib/systems/collisionSystem';
  import { audioSystem } from '$lib/systems/audioSystem';
  import { getRandomObjectForLetter, getRandomObjectNotForLetter } from '$lib/data/objectBank';
  import type { GameObject as GameObjectType } from '$lib/types';

  import StarField from '../ui/StarField.svelte';
  import Player from '../game/Player.svelte';
  import GameObject from '../game/GameObject.svelte';
  import ScoreDisplay from '../game/ScoreDisplay.svelte';
  import BackButton from '../game/BackButton.svelte';
  import AudioFallback from '../ui/AudioFallback.svelte';
  import AudioNotification from '../ui/AudioNotification.svelte';
  import ParticleSystem from '../effects/ParticleSystem.svelte';

  // Props
  interface Props {
    onBack?: () => void;
  }

  let { onBack = () => {} }: Props = $props();

  // Game state
  let playerX = $state(0);
  let playerY = $state(0);
  let objects = $state<Array<GameObjectType & { id: string }>>([]);
  let showPlayerPulse = $state(false);
  let nextObjectId = 0;

  // Audio state
  let audioAvailable = $state(true);
  let showAudioNotification = $state(false);
  let audioFallbackText = $state('');
  let showAudioFallback = $state(false);

  // Directional sound state
  let lastDirection = $state<'horizontal' | 'vertical' | null>(null);
  let lastSoundTime = $state(0);

  // Particle system reference
  let particleSystem: any;

  // Constants
  const PLAYER_SPEED = 480; // pixels per second (8 pixels per frame at 60 FPS)
  const MAX_OBJECTS = 10;
  const SPAWN_INTERVAL = 2000; // ms
  const CORRECT_RATIO = 0.4; // 40% of objects match player letter
  const POINTS_PER_HIT = 10;
  const STREAK_BONUS = 5;
  const SOUND_DEBOUNCE_MS = 2000; // Debounce time for directional sounds (2 seconds minimum spacing)

  // Initialize player position
  onMount(() => {
    playerX = window.innerWidth / 2;
    playerY = window.innerHeight / 2;

    // Check audio availability
    audioAvailable = audioSystem.isAudioAvailable();
    if (!audioAvailable) {
      showAudioNotification = true;
    }

    // Start systems
    inputSystem.start();
    gameEngine.startGameLoop(gameLoop);

    // Spawn initial objects
    spawnObjects(MAX_OBJECTS);

    // Set up periodic spawning
    const spawnTimer = setInterval(() => {
      if (objects.length < MAX_OBJECTS) {
        spawnObject();
      }
    }, SPAWN_INTERVAL);

    // Cleanup
    return () => {
      clearInterval(spawnTimer);
      inputSystem.stop();
      gameEngine.stopGameLoop();
    };
  });

  // Main game loop
  function gameLoop(deltaTime: number) {
    updatePlayerMovement(deltaTime);
    updateObjects(deltaTime);
    checkCollisions();
  }

  // Detect direction changes and play appropriate sounds
  function detectDirectionChange(input: { x: number; y: number }) {
    const now = performance.now();

    // Debounce check - prevent rapid-fire sounds
    if (now - lastSoundTime < SOUND_DEBOUNCE_MS) {
      return;
    }

    // Determine primary direction (horizontal vs vertical)
    const isHorizontal = Math.abs(input.x) > Math.abs(input.y);
    const currentDirection = isHorizontal ? 'horizontal' : 'vertical';

    // Only play sound on direction change (not continuously)
    if (currentDirection !== lastDirection && (input.x !== 0 || input.y !== 0)) {
      lastDirection = currentDirection;
      lastSoundTime = now;

      const letter = $gameState.player.currentLetter || 'A';
      const isCombination = letter.length > 1;

      if (isCombination) {
        // For combinations (CH, SH, TH):
        // Both directions play the combination phoneme sound
        audioSystem.playCombinationSound(letter, undefined, true);
      } else {
        // For single letters:
        if (isHorizontal) {
          // Left/Right movement → play letter NAME (immediate mode - drop if busy)
          audioSystem.playLetterName(letter, true);
        } else {
          // Up/Down movement → play letter PHONEME (immediate mode - drop if busy)
          audioSystem.playPhoneme(letter, 'short', true);
        }
      }
    }

    // Reset direction when no input
    if (input.x === 0 && input.y === 0) {
      lastDirection = null;
    }
  }

  // Update player position based on input
  function updatePlayerMovement(deltaTime: number) {
    if (!inputSystem.hasInput()) {
      // Reset direction when no input
      lastDirection = null;
      return;
    }

    const input = inputSystem.getInputVector();

    // Detect direction changes and play directional sounds
    detectDirectionChange(input);

    // deltaTime is in seconds, PLAYER_SPEED is pixels per second
    const distance = PLAYER_SPEED * deltaTime;

    playerX += input.x * distance;
    playerY += input.y * distance;

    // Clamp to screen boundaries
    const margin = 40;
    playerX = Math.max(margin, Math.min(window.innerWidth - margin, playerX));
    playerY = Math.max(margin, Math.min(window.innerHeight - margin, playerY));
  }

  // Update all objects (movement and cleanup)
  function updateObjects(deltaTime: number) {
    const now = performance.now();

    objects = objects.filter(obj => {
      // Remove if off-screen or too old
      const isOffScreen =
        obj.position.x < -100 ||
        obj.position.x > window.innerWidth + 100 ||
        obj.position.y < -100 ||
        obj.position.y > window.innerHeight + 100;

      const age = now - (obj as any).spawnTime;
      const isTooOld = age > 15000; // 15 seconds max

      return !isOffScreen && !isTooOld;
    });

    // Update positions based on velocity
    // deltaTime is in seconds, velocity is pixels per frame at 60 FPS
    // Convert: pixels/frame * 60 frames/sec = pixels/sec
    const velocityScale = 60; // Convert from pixels/frame to pixels/second
    objects = objects.map(obj => ({
      ...obj,
      position: {
        x: obj.position.x + obj.velocity.x * velocityScale * deltaTime,
        y: obj.position.y + obj.velocity.y * velocityScale * deltaTime
      }
    }));
  }

  // Check for collisions between player and objects
  function checkCollisions() {
    const playerLetter = $gameState.player.currentLetter || '';

    objects.forEach((obj, index) => {
      const isColliding = collisionSystem.checkPlayerCollision(
        { position: { x: playerX, y: playerY }, size: 80 },
        obj
      );

      if (isColliding) {
        const isCorrect = obj.startLetter === playerLetter;

        if (isCorrect) {
          // Correct hit!
          handleCorrectHit(obj, index);
        } else {
          // Incorrect hit
          handleIncorrectHit(obj, index);
        }
      }
    });
  }

  // Handle correct collision
  function handleCorrectHit(obj: GameObjectType & { id: string }, index: number) {
    const playerLetter = $gameState.player.currentLetter || 'A';

    // Award points
    let points = POINTS_PER_HIT;
    gameState.incrementStreak();

    // Streak bonus every 5 hits
    if ($gameState.streak > 0 && $gameState.streak % 5 === 0) {
      points += STREAK_BONUS;
    }

    gameState.addScore(points);

    // Audio feedback - play object name (e.g., "helicopter", "apple") - immediate mode to prevent queuing
    audioSystem.playObjectSound(obj.name, true);

    // Visual fallback if audio unavailable
    if (!audioAvailable) {
      audioFallbackText = obj.name;
      showAudioFallback = true;
      setTimeout(() => {
        showAudioFallback = false;
      }, 1000);
    }

    // Particle effects
    if (particleSystem) {
      // Burst at collision position
      particleSystem.createBurst(obj.position.x, obj.position.y);

      // Sparkle for streak milestones
      if ($gameState.streak > 0 && $gameState.streak % 5 === 0) {
        particleSystem.createSparkle(playerX, playerY);
      }
    }

    // Visual feedback
    showPlayerPulse = true;
    setTimeout(() => {
      showPlayerPulse = false;
    }, 300);

    // Remove object
    objects = objects.filter((_, i) => i !== index);

    // Spawn replacement
    setTimeout(() => spawnObject(), 500);
  }

  // Handle incorrect collision
  function handleIncorrectHit(obj: GameObjectType & { id: string }, index: number) {
    // Reset streak
    gameState.resetStreak();

    // Audio feedback - play object sound - immediate mode to prevent queuing
    audioSystem.playObjectSound(obj.name, true);

    // Visual fallback if audio unavailable
    if (!audioAvailable) {
      audioFallbackText = obj.name;
      showAudioFallback = true;
      setTimeout(() => {
        showAudioFallback = false;
      }, 1000);
    }

    // Bounce object away
    const dx = obj.position.x - playerX;
    const dy = obj.position.y - playerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    objects[index].velocity = {
      x: (dx / distance) * 3,
      y: (dy / distance) * 3
    };
  }

  // Spawn a single object
  function spawnObject() {
    const playerLetter = $gameState.player.currentLetter || 'A';
    const shouldBeCorrect = Math.random() < CORRECT_RATIO;

    const objectData = shouldBeCorrect
      ? getRandomObjectForLetter(playerLetter)
      : getRandomObjectNotForLetter(playerLetter);

    if (!objectData) return;

    // Random spawn position (around edges)
    const edge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
    let x = 0;
    let y = 0;
    let vx = 0;
    let vy = 0;

    const margin = 50;
    const width = window.innerWidth;
    const height = window.innerHeight;

    switch (edge) {
      case 0: // Top
        x = margin + Math.random() * (width - margin * 2);
        y = -margin;
        vx = (Math.random() - 0.5) * 2;
        vy = 1 + Math.random() * 2;
        break;
      case 1: // Right
        x = width + margin;
        y = margin + Math.random() * (height - margin * 2);
        vx = -(1 + Math.random() * 2);
        vy = (Math.random() - 0.5) * 2;
        break;
      case 2: // Bottom
        x = margin + Math.random() * (width - margin * 2);
        y = height + margin;
        vx = (Math.random() - 0.5) * 2;
        vy = -(1 + Math.random() * 2);
        break;
      case 3: // Left
        x = -margin;
        y = margin + Math.random() * (height - margin * 2);
        vx = 1 + Math.random() * 2;
        vy = (Math.random() - 0.5) * 2;
        break;
    }

    const newObject: GameObjectType & { id: string } = {
      id: `obj-${nextObjectId++}`,
      position: { x, y },
      velocity: { x: vx, y: vy },
      emoji: objectData.emoji,
      startLetter: objectData.letter,
      name: objectData.name,
      size: 60,
      spawnTime: performance.now()
    } as any;

    objects = [...objects, newObject];
  }

  // Spawn multiple objects
  function spawnObjects(count: number) {
    for (let i = 0; i < count; i++) {
      setTimeout(() => spawnObject(), i * 200);
    }
  }

  // Check if object is near player (for glow effect)
  function isNearPlayer(obj: GameObjectType): boolean {
    const dx = obj.position.x - playerX;
    const dy = obj.position.y - playerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < 150; // Glow threshold
  }
</script>

<div class="letter-mode-screen">
  <StarField />

  <!-- UI Header -->
  <div class="header">
    <div class="header-left">
      <BackButton onclick={onBack} />
      <div class="current-letter">
        Letter: <span class="letter">{$gameState.player.currentLetter || 'A'}</span>
      </div>
    </div>

    <div class="header-right">
      <ScoreDisplay score={$gameState.score} streak={$gameState.streak} />
    </div>
  </div>

  <!-- Game Area -->
  <div class="game-area">
    <!-- Player -->
    <Player
      letter={$gameState.player.currentLetter || 'A'}
      x={playerX}
      y={playerY}
      showPulse={showPlayerPulse}
    />

    <!-- Game Objects -->
    {#each objects as obj (obj.id)}
      <GameObject
        emoji={obj.emoji}
        name={obj.name}
        x={obj.position.x}
        y={obj.position.y}
        showGlow={isNearPlayer(obj)}
      />
    {/each}
  </div>

  <!-- Audio Fallback (shows letter/word visually when audio unavailable) -->
  <AudioFallback
    text={audioFallbackText}
    visible={showAudioFallback}
  />

  <!-- Audio Notification (persistent info banner) -->
  <AudioNotification
    visible={showAudioNotification}
    onDismiss={() => showAudioNotification = false}
  />

  <!-- Particle System -->
  <ParticleSystem bind:this={particleSystem} />
</div>

<style>
  .letter-mode-screen {
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }

  .header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-md);
    background: rgba(10, 22, 40, 0.85);
    backdrop-filter: blur(4px);
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: var(--space-lg);
  }

  .current-letter {
    font-size: var(--font-lg);
    color: var(--color-text-primary);
    font-weight: 600;
  }

  .letter {
    font-size: 36px;
    font-weight: bold;
    color: var(--color-bright-yellow);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  .header-right {
    display: flex;
    align-items: center;
  }

  .game-area {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  /* Responsive */
  @media (max-width: 767px) {
    .header {
      height: 50px;
      padding: var(--space-sm);
    }

    .current-letter {
      font-size: var(--font-body);
    }

    .letter {
      font-size: 28px;
    }

    .header-left {
      gap: var(--space-md);
    }
  }
</style>
